import React, { useState, useEffect } from 'react';
import {
  SaveOutlined,
  PrinterOutlined,
  PlusOutlined,
  CloseOutlined,
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import {
  Input,
  Button,
  Select,
  DatePicker,
  Card,
  Typography,
  Space,
  message,
  Row,
  Col,
  Table,
  InputNumber,
  Upload,
  Tag
} from 'antd';
import dayjs from 'dayjs';
// import { getVehicleInsurances } from '../Services/Services';
import { getParties, getBillsByParty, createReceipt, getVehicleInsurances, getReceipts } from '../Services/Services';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PaymentIn = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [partyList, setPartyList] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [partyBills, setPartyBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [billItems, setBillItems] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [allVehicles, setAllVehicles] = useState([]);

  const [formData, setFormData] = useState({
    receipt_no: '',
    receipt_date: dayjs(),
    received_from: '',
    mobile: '',
    address: '',
    rec_mode: 'Cash',
    rec_category: 'Insurance Commission',
    ref_no: '',
    ref_date: null,
    amount: 0,
    narration: '',
    bill_id: null,
    allocated_amounts: {}
  });

  // Fetch parties first
  useEffect(() => {
    loadPartiesAndCheckEditData();
    fetchNextReceiptNo();
  }, []);

  const fetchNextReceiptNo = async () => {
    try {
      const data = await getReceipts();

      setFormData(prev => ({
        ...prev,
        receipt_no: data.next_receipt_no
      }));

    } catch (error) {
      console.log(error);
    }
  };

  const loadPartiesAndCheckEditData = async () => {
    setLoading(true);
    try {
      // First fetch parties
      const data = await getParties();
      const vehicleResponse = await getVehicleInsurances({}, 1, 1000);

      setAllVehicles(vehicleResponse.results || []);

      let parties = [];
      if (data && data.results) {
        parties = data.results;
      } else if (data && Array.isArray(data)) {
        parties = data;
      }
      setPartyList(parties);

      // Now check for edit data in sessionStorage
      const editData = sessionStorage.getItem('editBillData');
      if (editData) {
        const { bill, isEditMode: editModeFlag } = JSON.parse(editData);
        if (editModeFlag && bill) {
          // Find the party from the bill
          const party = parties.find(p => p.partyName === bill.party_name);

          if (party) {
            setSelectedParty(party);
            setSelectedBill(bill);
            setIsEditMode(true);

            // Set form data with bill details
            const items = bill.items || [];
            const allocatedAmounts = {};

            items.forEach(item => {
              allocatedAmounts[item.id] = {
                amount: 0,
                maxAmount: parseFloat(item.amount),
                vehicle_no: item.vehicle_no,
                description: item.description,
                bill_item_id: item.id
              };
            });

            setBillItems(items);
            setFormData({
              receipt_no: '',
              receipt_date: dayjs(),
              received_from: party.partyName,
              mobile: party.mobileNumber || '',
              address: party.address || '',
              rec_mode: 'Cash',
              rec_category: 'Insurance Commission',
              ref_no: '',
              ref_date: null,
              amount: 0,
              narration: `Payment for Bill ${bill.bill_id}`,
              bill_id: bill.id,
              allocated_amounts: allocatedAmounts
            });

            message.info(`Creating receipt for Bill ${bill.bill_id}`);
          } else {
            message.error("Party not found for this bill");
          }

          // Clear sessionStorage after loading
          sessionStorage.removeItem('editBillData');
        }
      }
      setIsDataLoaded(true);
    } catch (error) {
      console.error("Error loading parties:", error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handlePartySelect = async (partyId) => {
    const party = partyList.find(p => p.id === partyId);
    if (!party) return;

    setSelectedParty(party);
    setSelectedBill(null);
    setBillItems([]);
    setUploadedFiles([]);
    setFormData({
      ...formData,
      received_from: party.partyName,
      mobile: party.mobileNumber || '',
      address: party.address || '',
      bill_id: null,
      allocated_amounts: {},
      amount: 0,
      narration: ''
    });

    setLoadingBills(true);
    try {
      const bills = await getBillsByParty(partyId);
      const filteredBills = bills.results || bills || [];
      // Only show pending bills that have balance
      const pendingBills = filteredBills.filter(bill => bill.is_pending === true);
      setPartyBills(pendingBills);
      if (pendingBills.length === 0) {
        message.info("No pending bills found for this party");
      }
    } catch (error) {
      console.error("Fetch bills error:", error);
      message.error("Failed to fetch party bills");
    } finally {
      setLoadingBills(false);
    }
  };

  const handleBillSelect = (billId) => {
    const bill = partyBills.find(b => b.id === billId);
    if (!bill) {
      console.error("Bill not found:", billId);
      return;
    }

    setSelectedBill(bill);
    const items = bill.items || [];
    const allocatedAmounts = {};

    items.forEach(item => {
      allocatedAmounts[item.id] = {
        amount: 0,
        maxAmount: parseFloat(item.amount),
        vehicle_no: item.vehicle_no,
        description: item.description,
        bill_item_id: item.id
      };
    });

    setBillItems(items);
    setFormData(prev => ({
      ...prev,
      bill_id: bill.id,
      allocated_amounts: allocatedAmounts,
      amount: 0,
      narration: `Payment for Bill ${bill.bill_id}`
    }));
  };

  const updateItemAmount = (itemId, amount) => {
    const newAmount = amount || 0;
    const updatedAllocated = { ...formData.allocated_amounts };
    const item = updatedAllocated[itemId];

    if (newAmount > item.maxAmount) {
      message.error(`Amount cannot exceed ₹${item.maxAmount.toLocaleString('en-IN')}`);
      return;
    }

    updatedAllocated[itemId] = { ...item, amount: newAmount };
    const totalAllocated = Object.values(updatedAllocated).reduce((sum, i) => sum + i.amount, 0);

    setFormData(prev => ({
      ...prev,
      allocated_amounts: updatedAllocated,
      amount: totalAllocated
    }));
  };

  const autoAllocateRemaining = () => {
    if (!selectedBill) return;

    const updatedAllocated = { ...formData.allocated_amounts };
    let totalAllocated = 0;

    Object.keys(updatedAllocated).forEach(itemId => {
      const item = updatedAllocated[itemId];
      updatedAllocated[itemId] = { ...item, amount: item.maxAmount };
      totalAllocated += item.maxAmount;
    });

    setFormData(prev => ({
      ...prev,
      allocated_amounts: updatedAllocated,
      amount: totalAllocated
    }));
    message.success(`Auto-allocated ₹${totalAllocated.toLocaleString('en-IN')}`);
  };

  const clearAllocations = () => {
    const clearedAllocations = {};
    billItems.forEach(item => {
      clearedAllocations[item.id] = {
        ...formData.allocated_amounts[item.id],
        amount: 0
      };
    });
    setFormData(prev => ({
      ...prev,
      allocated_amounts: clearedAllocations,
      amount: 0
    }));
    message.info("Allocations cleared");
  };

  const handleFileUpload = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      message.error(`${file.name} exceeds 5MB limit`);
      return false;
    }
    const previewUrl = URL.createObjectURL(file);
    setUploadedFiles(prev => [...prev, {
      id: Date.now(),
      file: file,
      name: file.name,
      type: file.type,
      size: file.size,
      preview: previewUrl
    }]);
    return false;
  };

  const removeFile = (fileId) => {
    const fileToRemove = uploadedFiles.find(f => f.id === fileId);
    if (fileToRemove?.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    message.success("File removed");
  };

  const handleSaveForm = async () => {
    // Validation
    if (!selectedParty) {
      message.error("Please select a Party");
      return;
    }
    if (!selectedBill) {
      message.error("Please select a Bill");
      return;
    }
    if (!formData.amount || formData.amount <= 0) {
      message.error("Please enter valid amount");
      return;
    }

    const balanceAmount = selectedBill.total_amount - (selectedBill.receipt_amount || 0);
    if (formData.amount > balanceAmount) {
      message.error(`Receipt amount cannot exceed balance amount of ₹${balanceAmount.toLocaleString('en-IN')}`);
      return;
    }

    setLoading(true);

    // Prepare receipt data
    const receiptData = {
      receipt_date: formData.receipt_date.format('YYYY-MM-DD'),
      received_from: selectedParty.partyName,
      mobile: selectedParty.mobileNumber || '',
      address: selectedParty.address || '',
      rec_mode: formData.rec_mode,
      rec_category: formData.rec_category,
      ref_no: formData.ref_no || '',
      ref_date: formData.ref_date ? formData.ref_date.format('YYYY-MM-DD') : null,
      amount: parseFloat(formData.amount),
      narration: formData.narration || `Payment for Bill ${selectedBill.bill_id}`,
      bill_id: selectedBill.id,
      allocated_items: Object.entries(formData.allocated_amounts)
        .filter(([_, data]) => data.amount > 0)
        .map(([itemId, data]) => ({
          bill_item_id: parseInt(itemId),
          amount: parseFloat(data.amount),
          vehicle_no: data.vehicle_no
        }))
    };

    console.log("Sending receipt data:", receiptData);

    try {
      const response = await createReceipt(receiptData);
      console.log("Receipt response:", response);

      message.success(`Payment Received Successfully! Receipt No: ${response.receipt_no || 'Generated'}`);

      // Clean up file previews
      uploadedFiles.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });

      // Reset form or go back
      if (onBack) {
        onBack();
      } else {
        resetForm();
      }

    } catch (error) {
      console.error("Save receipt error:", error);
      message.error(error.response?.data?.error || error.response?.data?.message || "Failed to save receipt");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedParty(null);
    setSelectedBill(null);
    setBillItems([]);
    setPartyBills([]);
    setUploadedFiles([]);
    setIsEditMode(false);
    setFormData({
      receipt_no: '',
      receipt_date: dayjs(),
      received_from: '',
      mobile: '',
      address: '',
      rec_mode: 'Cash',
      rec_category: 'Insurance Commission',
      ref_no: '',
      ref_date: null,
      amount: 0,
      narration: '',
      bill_id: null,
      allocated_amounts: {}
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const billColumns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 60,
      render: (_, __, idx) => idx + 1
    },
    {
      title: 'Vehicle No',
      dataIndex: 'vehicle_no',
      render: (text) => text || '-'
    },
    {
      title: 'Chassis No',
      key: 'chassis',
      render: (_, record) => {

        const vehicle = allVehicles.find(
          v => v.id === record.vehicle_id
        );

        return vehicle?.chassisNumber || '-';
      }
    },

    {
      title: 'Description',
      dataIndex: 'description',
      render: (text) => text || '-'
    },
    {
      title: 'Bill Amount',
      dataIndex: 'amount',
      render: (text) => `₹${parseFloat(text).toLocaleString('en-IN')}`
    },
    {
      title: 'Received Amount',
      key: 'received',
      render: (_, record) => (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="0"
          value={formData.allocated_amounts[record.id]?.amount || 0}
          onChange={(val) => updateItemAmount(record.id, val)}
          min={0}
          max={parseFloat(record.amount)}
          size="small"
          formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value.replace(/₹\s?|(,*)/g, '')}
        />
      )
    },
    {
      title: 'Remaining',
      key: 'remaining',
      render: (_, record) => {
        const allocated = formData.allocated_amounts[record.id]?.amount || 0;
        const remaining = parseFloat(record.amount) - allocated;
        return (
          <Tag color={remaining === 0 ? 'success' : 'warning'}>
            ₹{remaining.toLocaleString('en-IN')}
          </Tag>
        );
      }
    }
  ];

  if (!isDataLoaded && loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Text>Loading...</Text>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '16px' }}>

      {/* Header with Back Button */}
      {onBack && (
        <div style={{ marginBottom: 16 }}>
          <Button onClick={onBack} type="default">
            ← Back
          </Button>
          {isEditMode && (
            <Tag color="orange" style={{ marginLeft: 12 }}>
              Creating Receipt for Bill: {selectedBill?.bill_id}
            </Tag>
          )}
        </div>
      )}

      {/* Party Information Card */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={[12, 8]}>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Select Party *</Text>
              <Select
                placeholder="-- Select Party --"
                value={selectedParty?.id}
                onChange={handlePartySelect}
                style={{ width: '100%', marginTop: 4 }}
                showSearch
                size="middle"
                optionFilterProp="children"
                disabled={isEditMode}
              >
                {partyList.map((party) => (
                  <Option key={party.id} value={party.id}>
                    {party.partyName} - {party.mobileNumber}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Mobile</Text>
              <Input value={formData.mobile} disabled size="middle" style={{ marginTop: 4 }} />
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Receipt No.</Text>
              <Input
                placeholder="Auto / Manual"
                value={formData.receipt_no}
                onChange={(e) => handleChange('receipt_no', e.target.value)}
                size="middle"
                style={{ marginTop: 4 }}
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Receipt Date</Text>
              <DatePicker
                style={{ width: '100%', marginTop: 4 }}
                value={formData.receipt_date}
                onChange={(date) => handleChange('receipt_date', date)}
                format="DD/MM/YYYY"
                size="middle"
              />
            </div>
          </Col>

          <Col xs={24}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Address</Text>
              <Input value={formData.address} disabled size="middle" style={{ marginTop: 4 }} />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Bill Selection - Only show in create mode */}
      {selectedParty && !isEditMode && (
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
          <div>
            <Text strong style={{ fontSize: 12 }}>Select Bill *</Text>
            <Select
              placeholder="-- Select Bill --"
              value={selectedBill?.id}
              onChange={handleBillSelect}
              style={{ width: '100%', marginTop: 4 }}
              loading={loadingBills}
              size="middle"
            >
              {partyBills.map((bill) => {
                const balance = bill.total_amount - (bill.receipt_amount || 0);
                return (
                  <Option key={bill.id} value={bill.id}>
                    Bill #{bill.bill_id} - {bill.bill_date} -
                    Total: ₹{parseFloat(bill.total_amount).toLocaleString('en-IN')} |
                    Balance: ₹{balance.toLocaleString('en-IN')}
                  </Option>
                );
              })}
            </Select>
            {selectedBill && (
              <div style={{ marginTop: 8, padding: 8, background: '#f0f5ff', borderRadius: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Selected Bill: {selectedBill.bill_id} |
                  Total: ₹{selectedBill.total_amount.toLocaleString('en-IN')} |
                  Paid: ₹{(selectedBill.receipt_amount || 0).toLocaleString('en-IN')} |
                  Balance: ₹{(selectedBill.total_amount - (selectedBill.receipt_amount || 0)).toLocaleString('en-IN')}
                </Text>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Bill Items Table */}
      {selectedBill && billItems.length > 0 && (
        <Card
          size="small"
          style={{ marginBottom: 16, borderRadius: 8 }}
          extra={
            !isEditMode && (
              <Space size="small">
                <Button size="small" icon={<PlusOutlined />} onClick={autoAllocateRemaining}>
                  Auto Allocate
                </Button>
                <Button size="small" icon={<CloseOutlined />} onClick={clearAllocations} danger>
                  Clear
                </Button>
              </Space>
            )
          }
        >
          <Table
            columns={billColumns}
            dataSource={billItems}
            rowKey="id"
            pagination={false}
            size="small"
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Text strong>Summary</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <Text strong>Total: ₹{selectedBill.total_amount.toLocaleString('en-IN')}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <div>
                      <Text strong>Received: </Text>
                      <InputNumber
                        style={{ width: '120px' }}
                        value={formData.amount}
                        onChange={(val) => handleChange('amount', val)}
                        min={0}
                        max={selectedBill.total_amount - (selectedBill.receipt_amount || 0)}
                        size="small"
                        placeholder="Amount"
                        formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value.replace(/₹\s?|(,*)/g, '')}
                      />
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    <Tag color="orange">
                      Balance: ₹{(selectedBill.total_amount - (selectedBill.receipt_amount || 0) - (formData.amount || 0)).toLocaleString('en-IN')}
                    </Tag>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>
      )}

      {/* Payment Details Card */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={[12, 8]}>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Payment Mode</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={formData.rec_mode}
                onChange={(val) => handleChange('rec_mode', val)}
                size="middle"
              >
                <Option value="Cash">Cash</Option>
                <Option value="UPI / GooglePay">UPI / GooglePay</Option>
                <Option value="Bank Deposit">Bank Deposit</Option>
                <Option value="Cheque">Cheque</Option>
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Receipt Category</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={formData.rec_category}
                onChange={(val) => handleChange('rec_category', val)}
                size="middle"
              >
                <Option value="Insurance Commission">Insurance Commission</Option>
                <Option value="Service Fee">Service Fee</Option>
                <Option value="Registration">Registration</Option>
                <Option value="Tax Payment">Tax Payment</Option>
                <Option value="Other">Other</Option>
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Reference No (UTR/Cheque)</Text>
              <Input
                placeholder="Optional"
                value={formData.ref_no}
                onChange={(e) => handleChange('ref_no', e.target.value)}
                size="middle"
                style={{ marginTop: 4 }}
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Reference Date</Text>
              <DatePicker
                style={{ width: '100%', marginTop: 4 }}
                value={formData.ref_date}
                onChange={(date) => handleChange('ref_date', date)}
                format="DD/MM/YYYY"
                size="middle"
              />
            </div>
          </Col>

          <Col xs={24}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Narration / Remarks</Text>
              <TextArea
                rows={2}
                value={formData.narration}
                onChange={(e) => handleChange('narration', e.target.value)}
                placeholder="Additional notes..."
                style={{ marginTop: 4 }}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* File Upload Card */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
        <div>
          <Upload
            beforeUpload={handleFileUpload}
            accept="image/jpeg,image/png,image/jpg,application/pdf"
            showUploadList={false}
            multiple
          >
            <Button icon={<UploadOutlined />}>Upload Attachments</Button>
          </Upload>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
            JPEG, PNG, PDF - Max 5MB
          </Text>
        </div>

        {uploadedFiles.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {uploadedFiles.map(file => (
              <div key={file.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <FileTextOutlined />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12 }}>{file.name}</div>
                  <div style={{ fontSize: 10, color: '#999' }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => window.open(file.preview, '_blank')} />
                <Button type="text" size="small" icon={<DeleteOutlined />} onClick={() => removeFile(file.id)} danger />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Action Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <div>
          <Text strong style={{ fontSize: 18, color: '#1e3a8a' }}>
            Total Receipt Amount: ₹{(formData.amount || 0).toLocaleString('en-IN')}
          </Text>
        </div>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSaveForm}
          loading={loading}
          size="large"
          disabled={!selectedBill || formData.amount <= 0}
        >
          {loading ? 'SAVING...' : 'SAVE RECEIPT'}
        </Button>
      </div>
    </div>
  );
};

export default PaymentIn;